import { SendOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Input, Modal, Skeleton, Table, Tabs, Typography } from "antd";
import { type Key, type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router";
import Page from "~Common/Page";
import PermissionCheck from "~Common/PermissionCheck";
import CustomSelect from "~components/CustomSelect";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UserRoles } from "~enums/userRoles";
import { userRolesConfig } from "../constants/userRolesConfig";
import type { PermissionProducts } from "../enums/permissionProducts";
import { useFetchUser, useInviteNotifyUser } from "../hooks";
import type { UserPermissionTransformed } from "../types";
import { ViewUserPermissionModal } from "./components";
import { useDeleteUserPermission, useGetUserPermissionsTableColumns } from "./hooks";
import type { PermissionProductTabOption } from "./types";
import { getPermissionProductsTabOptions, getUserRoleSelectOptionsByProduct, groupPermissionsByProduct } from "./utilities";

import "./PermissionUserDetailsPage.scss";

const PermissionUserDetailsPage = () => {
  const { formatMessage } = useIntl();
  const [selectedRows, setSelectedRows] = useState<Key[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId, sport, organizationId } = useParams();
  const { userRole: routeUserRole } = location?.state || {};
  const routeProduct = routeUserRole ? userRolesConfig[routeUserRole]?.product : "";
  const { data: user, isLoading } = useFetchUser();

  // biome-ignore lint/correctness/useExhaustiveDependencies: this hook should run on nested properties change in user permissions
  const permissionsByProduct = useMemo(() => groupPermissionsByProduct(user?.permissions || []), [JSON.stringify(user?.permissions)]);
  const availableProducts = Object.keys(permissionsByProduct) as PermissionProducts[];

  const productTabOptions = useMemo<PermissionProductTabOption[]>(
    () => getPermissionProductsTabOptions(availableProducts, formatMessage),
    [availableProducts, formatMessage],
  );
  const [selectedProductTab, setSelectedProductTab] = useState<PermissionProducts>(routeProduct);
  const [selectedUserRoles, setSelectedUserRoles] = useState<UserRoles[]>(routeUserRole ? [routeUserRole] : []);
  const currentProductTab = selectedProductTab || availableProducts[0];
  const { mutateAsync: deleteUserPermission } = useDeleteUserPermission();
  const handleInviteNotifyUser = useInviteNotifyUser();
  const [currentUserPermissionForView, setCurrentUserPermissionForView] = useState<UserPermissionTransformed | null>(null);
  const [modal, modalContextHolder] = Modal.useModal();

  const userRoleOptionsByProduct = useMemo(() => getUserRoleSelectOptionsByProduct(formatMessage), [formatMessage]);
  const filteredPermissions = useMemo<UserPermissionTransformed[]>(() => {
    const productPermissions = permissionsByProduct[currentProductTab] || [];
    if (selectedUserRoles.length > 0) {
      return productPermissions.filter((permission) => selectedUserRoles.includes(permission.userRole as UserRoles));
    }
    return productPermissions;
  }, [selectedUserRoles, currentProductTab, permissionsByProduct[currentProductTab]]);

  const onViewPermissionClick = (userPermission: UserPermissionTransformed) => setCurrentUserPermissionForView(userPermission);

  const onDeletePermissionClick = (userRole: string, permissionId: number) =>
    modal.confirm({
      title: formatMessage({
        id: "permissions.user.role.actions.delete.title",
      }),
      content: formatMessage(
        { id: "permissions.user.role.actions.delete.content" },
        { roleName: formatMessage({ id: `permissions.userRole.${userRole}` }) },
      ),
      okText: formatMessage({ id: "delete" }),
      onOk: async () =>
        deleteUserPermission(permissionId).then(() => setSelectedRows((prevState) => prevState.filter((item) => item !== permissionId))),
      centered: true,
    });

  // biome-ignore lint/style/noNonNullAssertion: this value is guaranteed to exist here
  const handleInviteNotifyClick = (e: MouseEvent) => handleInviteNotifyUser(e, user!.userId, user!.isPending, selectedRows as number[]);

  const handleProductTabChange = useCallback((product: PermissionProducts) => {
    setSelectedProductTab(product);
    setSelectedUserRoles([]);
    setSelectedRows([]);
  }, []);

  const onSelectRowChange = (newSelectedRowKeys: Key[]) => setSelectedRows(newSelectedRowKeys);

  const columns = useGetUserPermissionsTableColumns(currentProductTab, onDeletePermissionClick, onViewPermissionClick);

  let pageTitle = formatMessage({ id: "permissions.user.actions.edit" });
  if (user?.name) {
    pageTitle += ` — ${user.name}`;
  }

  const goBackToUserList = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/permissions`);

  useEffect(() => {
    if (currentProductTab && !availableProducts.includes(currentProductTab)) {
      handleProductTabChange(availableProducts[0]);
    }
  }, [currentProductTab, availableProducts, handleProductTabChange]);

  return (
    <Page title={pageTitle} subtitle={formatMessage({ id: "permissions.user.actions.edit.subtitle" })} onBack={goBackToUserList}>
      {modalContextHolder}
      <Typography.Title level={4}>{formatMessage({ id: "basic.details" })}</Typography.Title>
      <Flex gap={24}>
        <div style={{ minWidth: "250px" }}>
          <Typography.Text>{formatMessage({ id: "name" })}</Typography.Text>
          {isLoading ? <Skeleton.Input block active size="large" /> : <Input size="large" disabled value={user?.name} />}
        </div>
        <div style={{ minWidth: "250px" }}>
          <Typography.Text>{formatMessage({ id: "email" })}</Typography.Text>
          {isLoading ? <Skeleton.Input block active size="large" /> : <Input size="large" disabled value={user?.email} />}
        </div>
      </Flex>
      <Divider />
      <Flex justify="space-between" align="center">
        <Typography.Title level={5}>{formatMessage({ id: "permissions.user.roles.assigned" })}</Typography.Title>
        <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.CREATE]}>
          <Button
            disabled={isLoading}
            onClick={() => navigate("permission")}
            icon={<i className="fas fa-plus" style={{ marginRight: "5px" }} />}
            type="primary"
          >
            {formatMessage({ id: "permissions.user.role.actions.add" })}
          </Button>
        </PermissionCheck>
      </Flex>
      <Tabs
        rootClassName="permission-user-role-tabs"
        size="large"
        activeKey={currentProductTab}
        onChange={(value) => handleProductTabChange(value as PermissionProducts)}
        items={productTabOptions}
      />
      <Flex align="center" justify="space-between">
        {currentProductTab && (
          <CustomSelect
            mode="multiple"
            maxTagCount="responsive"
            value={selectedUserRoles}
            onChange={(userRoles) => setSelectedUserRoles(userRoles as UserRoles[])}
            options={userRoleOptionsByProduct[currentProductTab] || []}
            allowClear
            placeholder={formatMessage({ id: "permissions.user.role.type" })}
            disabled={isLoading}
            className="permissions-user-role-select"
          />
        )}
        <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.CREATE]}>
          <Button
            disabled={isLoading || (!user?.isPending && selectedRows.length === 0)}
            onClick={handleInviteNotifyClick}
            icon={<SendOutlined style={{ marginRight: "5px" }} />}
            type="primary"
          >
            {formatMessage({ id: user?.isPending ? "invite" : "notify" })}
          </Button>
        </PermissionCheck>
      </Flex>
      <Table
        columns={columns}
        rowSelection={{
          selectedRowKeys: selectedRows,
          onChange: onSelectRowChange,
        }}
        dataSource={filteredPermissions}
        loading={isLoading}
        pagination={false}
        rowKey="permissionId"
        size="small"
      />
      <ViewUserPermissionModal
        userPermission={currentUserPermissionForView}
        onClose={() => setCurrentUserPermissionForView(null)}
        isOpen={!!currentUserPermissionForView}
      />
    </Page>
  );
};

export default PermissionUserDetailsPage;
